import React from 'react';
import PropTypes from 'prop-types';
import sortBy from 'lodash/sortBy';
import TabNavigation from '../../components/TabNavigation/TabNavigation';
import YPCustomDataElement from '../../components/YPCustomDataElement/YPCustomDataElement';
import YPTimeSchedulesTable from '../../components/YPTimeSchedulesTable/YPTimeSchedulesTable';
import { getFullEventWidgetTabName } from '../../libs/tabFormatter';
import Button from '../../components/Button/Button';
import List from '../../styled components/List';
import ListItem from '../../components/ListItem/ListItem';
import {
  categorizeBookingsPerGroup,
  groupAndSortGeneratedReports,
  sortContactBookingsOnName
} from '../../libs/ypAPIDataFormatter';
import CustomDataReadOnlyField from '../../components/CustomDataReadOnlyField/CustomDataReadOnlyField';
import YPAttachment from '../../components/YPAttachment/YPAttachment';
import YPResourceBooking from '../../components/YPResourceBooking';
import SessionStorageContext from '../../components/SessionStorageContext';
import { sessionStorageItemNameEmptyCustomData } from '../../libs/sessionStorageItemNames';
import { Meta } from '../../styled components/Typography';
import {
  TitleHeaderForBookings,
  ListItemForYPData,
  ToggleCustomDataButton,
  ListItemResourceBooking
} from './EventDetailPageBody.style';
import SVGIcon from '../../components/SVGIcon/SVGIcon';
import ContainerWrapper from '../../styled components/ContainerWrapper';
import Sticky from '../../styled components/Sticky';
import NavigationHistory from '../../libs/NavigationHistory';

/**
 * Render the body of the event detail widget; the tab navigation and the actual content.
 * This component is responsible for displaying the default content for the selected tab,
 * as well as the custom data that can be listed on the tab.
 *
 * The default content for the tabs includes:
 *   - • Schedule overview
 *   - • List of booked contacts (merged with contact bookings of its parent events), sorted alphabetically
 *   - • List of booked resources (categorised on resource group)
 *   - • List of booked locations (categorised on resource group)
 *   - • List of booked personnel (categorised on resource group)
 *   - • List of attachments
 *   - • List of generated reports, grouped on report template with the last generated as main report
 *
 * The custom data is categorised on custom data groups and blocks and rendered beneath the default content.
 */
class EventDetailPageBody extends React.Component {
  constructor(props) {
    super(props);

    this.renderContent = this.renderContent.bind(this);

    this.tabHeaderRef = React.createRef();
    this.tabHeaderHeight = 0;
  }

  componentDidMount() {
    if (this.tabHeaderRef.current)
      this.tabHeaderHeight = this.tabHeaderRef.current.clientHeight;
  }

  topPositionForStickyHeader() {
    const { pageHeaderHeight } = this.props;
    return pageHeaderHeight + this.tabHeaderHeight;
  }

  renderContent(tab) {
    const { customDataPerTab, showEmptyCustomData, toggleShowEmptyCustomData } =
      this.props;
    const content = customDataPerTab[tab.tabid];
    return (
      <div data-test-container="eventdetail">
        {this.renderDefaultContentForTab(tab)}
        {content && (
          <>
            <div tw="flex justify-end">
              <ToggleCustomDataButton
                onClick={toggleShowEmptyCustomData}
                dataAttributes={{
                  'data-test-button': 'toggleShowAllCustomData'
                }}
              >
                {showEmptyCustomData ? i18n('Hide Empty') : i18n('Show Empty')}
              </ToggleCustomDataButton>
            </div>
            {content.map((cdgroup) => (
              <YPCustomDataElement
                key={cdgroup.keyword}
                customData={cdgroup}
                topPosition={this.topPositionForStickyHeader()}
                {...this.props}
              />
            ))}
          </>
        )}
      </div>
    );
  }

  renderDefaultContentForTab(tab) {
    switch (tab.tabid) {
      case getFullEventWidgetTabName('Schedules'):
        return this.renderSchedules(tab.tabid);
      case getFullEventWidgetTabName('Contacts'):
        return this.renderContactBookings(tab.tabid);
      case getFullEventWidgetTabName('Resources'):
        return this.renderResourceBookings(tab.tabid);
      case getFullEventWidgetTabName('Attachments'):
        return this.renderAttachments(tab.tabid);
      case getFullEventWidgetTabName('Reports'):
        return this.renderReports(tab.tabid);
      case getFullEventWidgetTabName('Location'):
        return this.renderResourceBookings(tab.tabid, 'Location');
      case getFullEventWidgetTabName('Personnel'):
        return this.renderResourceBookings(tab.tabid, 'Human', false);
      default:
        return null;
    }
  }

  renderEmptyTabText(translatedString) {
    return (
      <div tw="text-center mt-24 mb-48">
        <Meta>{translatedString}</Meta>
      </div>
    );
  }

  renderSchedules(tabId) {
    const { contentPerTab, event, customDataPerTab } = this.props;
    const schedules = contentPerTab[tabId];
    const customData = customDataPerTab[tabId];
    if (!schedules.hasEntries() && !customData)
      return this.renderEmptyTabText(i18n('There are no schedules for this event.'));
    if (schedules) {
      return (
        <>
          {/* spacer */}
          <div tw="pt-8" />
          <YPTimeSchedulesTable schedules={schedules} defaultDate={event.startDate} />
          {/* spacer */}
          <div tw="pb-24" />
        </>
      );
    }
    return null;
  }

  renderContactBookings(tabId) {
    const { contentPerTab, customDataPerTab } = this.props;
    const contactBookings = contentPerTab[tabId];
    const customData = customDataPerTab[tabId];
    if (contactBookings.length === 0 && !customData)
      return this.renderEmptyTabText(
        i18n('There are no contact bookings for this event.')
      );
    if (contactBookings.length > 0) {
      return (
        <>
          <List data-test-list="contacts">
            {sortContactBookingsOnName(contactBookings).map((booking) => (
              <ListItemForYPData
                key={booking.id}
                data-test-type="contactBooking"
                icon={booking.getIcon()}
              >
                <CustomDataReadOnlyField type="contact" value={booking} />
              </ListItemForYPData>
            ))}
          </List>
        </>
      );
    }
    return null;
  }

  renderResourceBookingInList(booking, collapseSets, resourceType) {
    const renderedBooking = (
      <YPResourceBooking
        key={booking.id}
        booking={booking}
        collapsed={collapseSets}
        resourceType={resourceType}
      />
    );
    /* Check if it is a set or no, and wrap it in a ListItem if not.
       Otherwise, sets would have a bullet from the ListItem and from its collapsible state icon */
    if (booking.isForSet()) return renderedBooking;
    return <ListItemResourceBooking key={booking.id}>{renderedBooking}</ListItemResourceBooking>;
  }

  renderBookingsInCategoryHeaders(
    categoryHeaders,
    bookingsPerGroup,
    collapseSets,
    resourceType
  ) {
    return categoryHeaders.map((groupName) => (
      <div tw="mb-8 ml-16" key={groupName} data-test-type="resourceGroup">
        <Sticky $zIndex={2} $top={this.topPositionForStickyHeader()}>
          <TitleHeaderForBookings label={groupName} />
        </Sticky>
        <List>
          {sortBy(bookingsPerGroup[groupName], [
            (booking) => booking.name.toUpperCase()
          ]).map((booking) =>
            this.renderResourceBookingInList(booking, collapseSets, resourceType)
          )}
        </List>
      </div>
    ));
  }

  renderResourceBookings(tabId, resourceType = null, collapseSets = true) {
    const { contentPerTab, customDataPerTab } = this.props;
    const bookings = contentPerTab[tabId];
    const customData = customDataPerTab[tabId];
    if (
      tabId === getFullEventWidgetTabName('Location') &&
      bookings.length === 0 &&
      !customData
    )
      return this.renderEmptyTabText(i18n('There is no location for this event.'));
    if (bookings.length === 0 && !customData)
      return this.renderEmptyTabText(
        i18n('There are no resource bookings for this event.')
      );
    const bookingsPerGroup = categorizeBookingsPerGroup(bookings);
    const categoryHeaders = Object.keys(bookingsPerGroup).sort();

    return (
      <div tw="pt-24" key={tabId}>
        {this.renderBookingsInCategoryHeaders(
          categoryHeaders,
          bookingsPerGroup,
          collapseSets,
          resourceType
        )}
      </div>
    );
  }

  renderAttachments(tabId) {
    const { contentPerTab, customDataPerTab } = this.props;
    const attachments = contentPerTab[tabId];
    const customData = customDataPerTab[tabId];
    if (attachments.length === 0 && !customData)
      return this.renderEmptyTabText(i18n('There are no attachments for this event.'));
    return (
      <>
        <List $withLeftPadding data-test-list="attachments">
          {attachments.map((attachment) => (
            <ListItem key={attachment.url} icon="bullet">
              <YPAttachment attachment={attachment} />
            </ListItem>
          ))}
        </List>
      </>
    );
  }

  renderReports(tabId) {
    const {
      contentPerTab,
      eventTree,
      eventTreeReports,
      event,
      fetchAllGeneratedReportsInEventHierarchy
    } = this.props;
    const reports = contentPerTab[tabId];
    const allEvents =
      eventTreeReports.length > 0 ? [...eventTreeReports].reverse() : [event];
    const renderMetaInfo = (group) => () =>
      (
        <Meta data-test-type="reportMetaInfo" $color="gray dark">
          {`${i18n('Added to event group:')} `}
          <span tw="align-text-bottom">
            <SVGIcon icon="tree-view" size="small" />
          </span>
          {` ${group.name}`}
        </Meta>
      );
    if (Object.values(reports).every((reportsForEvent) => reportsForEvent.length === 0))
      return this.renderEmptyTabText(
        i18n('There are no generated reports for this event.')
      );

    return (
      <>
        <List data-test-list="generatedreports">
          {allEvents.map((anEvent) => {
            const reportsForEvent = reports[anEvent.id];
            if (!reportsForEvent || reportsForEvent.length === 0) return null;
            return (
              <React.Fragment key={`reports-${anEvent.id}`}>
                {groupAndSortGeneratedReports(reportsForEvent).map(
                  (reportsPerTemplate) => {
                    const lastGenerated = reportsPerTemplate[0];
                    return (
                      <ListItemForYPData key={lastGenerated.id}>
                        <YPAttachment
                          attachment={lastGenerated}
                          olderAttachments={reportsPerTemplate.slice(1)}
                          renderMetaInfo={
                            anEvent.id !== event.id ? renderMetaInfo(anEvent) : null
                          }
                        />
                      </ListItemForYPData>
                    );
                  }
                )}
              </React.Fragment>
            );
          })}
        </List>
        {eventTreeReports.length < eventTree.length && (
          <>
            <ContainerWrapper>
              <div tw="flex justify-center mt-8 mb-24">
                <div tw="text-center md:max-w-3/5">
                  <Meta>
                    {i18n(
                      'Some inherited reports may be missing because this event has more than four parent event groups and they weren’t all checked for reports.'
                    )}
                  </Meta>
                  <div tw="flex justify-center mt-16">
                    <Button
                      size="medium"
                      color="primary"
                      variant="outlined"
                      onClick={() => {
                        fetchAllGeneratedReportsInEventHierarchy();
                      }}
                    >
                      {i18n('Check the Remaining Event Groups')}
                    </Button>
                  </div>
                </div>
              </div>
            </ContainerWrapper>
          </>
        )}
      </>
    );
  }

  render() {
    const {
      visibletabs,
      defaultTab,
      eventSummaryHeight,
      isCollapsibleHeaderShown,
      pageHeaderHeight,
      navigationHistory,
      widgetAnimationWrapperRef
    } = this.props;
    if (visibletabs.length === 0) return null;
    return (
      <>
        <div
          data-test-list="eventtabs"
          tw="md:max-w-5xl w-screen ml-auto mr-auto flex-grow bg-white"
        >
          <TabNavigation
            ref={this.tabHeaderRef}
            tabs={visibletabs}
            defaultTab={defaultTab}
            renderContent={this.renderContent}
            shouldAnimateTopPosition={isCollapsibleHeaderShown}
            animateTopPositionTo={eventSummaryHeight}
            stickyTopPosition={pageHeaderHeight}
            navigationHistory={navigationHistory}
            widgetAnimationWrapperRef={widgetAnimationWrapperRef}
          />
        </div>
      </>
    );
  }
}

EventDetailPageBody.propTypes = {
  visibletabs: PropTypes.arrayOf(PropTypes.shape({})),
  customDataPerTab: PropTypes.shape({}).isRequired,
  contentPerTab: PropTypes.shape({}).isRequired,
  pageHeaderHeight: PropTypes.number,
  defaultTab: PropTypes.shape({}).isRequired,
  isHeaderShown: PropTypes.bool,
  eventSummaryHeight: PropTypes.number,
  isCollapsibleHeaderShown: PropTypes.bool,
  contactLinkReferences: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  contactReferences: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  user: PropTypes.shape({
    preferences: PropTypes.shape({
      defaultEventInspectorTabApp: PropTypes.shape({})
    })
  }).isRequired,
  event: PropTypes.shape({
    startdate: PropTypes.string,
    id: PropTypes.string
  }).isRequired,
  eventTree: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string
    })
  ).isRequired,
  fetchAllGeneratedReportsInEventHierarchy: PropTypes.func.isRequired,
  eventTreeReports: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  showEmptyCustomData: PropTypes.bool.isRequired,
  toggleShowEmptyCustomData: PropTypes.func.isRequired,
  widgetAnimationWrapperRef: PropTypes.shape({
    current: PropTypes.oneOfType([() => null, PropTypes.shape({})])
  }).isRequired,
  navigationHistory: PropTypes.instanceOf(NavigationHistory).isRequired
};

EventDetailPageBody.defaultProps = {
  pageHeaderHeight: 0,
  visibletabs: [],
  isHeaderShown: false,
  eventSummaryHeight: 0,
  isCollapsibleHeaderShown: false
};

export default (props) => (
  <SessionStorageContext.Consumer>
    {({ getSessionStorageValue, setSessionStorageValue }) => (
      <EventDetailPageBody
        {...props}
        showEmptyCustomData={getSessionStorageValue(
          sessionStorageItemNameEmptyCustomData
        )}
        toggleShowEmptyCustomData={() => {
          const current = getSessionStorageValue(sessionStorageItemNameEmptyCustomData);
          setSessionStorageValue(sessionStorageItemNameEmptyCustomData, !current);
        }}
      />
    )}
  </SessionStorageContext.Consumer>
);
